import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Post } from '../../types/post';

interface EditPostModalProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => Promise<void>;
}

export function EditPostModal({ post, isOpen, onClose, onSave }: EditPostModalProps) {
  const { theme } = useTheme();
  const [title, setTitle] = useState(post.title || '');
  const [content, setContent] = useState(post.content);
  const [mediaUrl, setMediaUrl] = useState(post.mediaUrl || post.imageUrl || '');
  const [removeMedia, setRemoveMedia] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      setTitle(post.title || '');
      setContent(post.content);
      setMediaUrl(post.mediaUrl || post.imageUrl || '');
      setRemoveMedia(false);
    }
  }, [isOpen, post]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        title: post.title !== undefined ? title : undefined,
        content,
        mediaUrl: removeMedia ? undefined : mediaUrl,
        removeMedia
      });
      onClose();
    } catch (error) {
      alert('Failed to save post. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="max-w-2xl w-full rounded-lg backdrop-blur-sm border shadow-2xl"
        style={{
          background: theme.cardBg.replace(/[\d.]+\)$/, '0.95)'),
          borderColor: theme.accentColor + '40',
          transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseMove={(e) => {
          if (isDragging) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            setModalPosition({ x: modalPosition.x + deltaX, y: modalPosition.y + deltaY });
            setDragStart({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
      >
        <div
          className="flex items-center justify-between p-6 pb-4 cursor-grab active:cursor-grabbing"
          onMouseDown={(e) => {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
          }}
          style={{
            borderBottom: `1px solid ${theme.accentColor}20`,
            userSelect: 'none'
          }}
        >
          <h3 className="text-xl font-bold" style={{ color: theme.textColor }}>Edit Post</h3>
          <button
            onClick={onClose}
            className="transition-colors hover:opacity-70"
            style={{ color: theme.textColor }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {post.title !== undefined && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.textColor + 'cc' }}>
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg outline-none transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: theme.textColor,
                  border: `1px solid ${theme.accentColor}40`
                }}
                placeholder="Post title"
                maxLength={100}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.textColor + 'cc' }}>
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 rounded-lg outline-none resize-none transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`
              }}
              rows={6}
              maxLength={5000}
              placeholder="Post content"
            />
            <div className="text-right text-sm mt-1" style={{ color: theme.textColor + '99' }}>
              {content.length}/5000
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.textColor + 'cc' }}>
              Media URL
            </label>
            <input
              type="text"
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              disabled={removeMedia}
              className="w-full px-3 py-2 rounded-lg outline-none transition-all"
              style={{
                backgroundColor: removeMedia ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`,
                opacity: removeMedia ? 0.5 : 1
              }}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          {(post.mediaUrl || post.imageUrl) && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="removeMedia"
                checked={removeMedia}
                onChange={(e) => setRemoveMedia(e.target.checked)}
                className="w-4 h-4"
              />
              <label
                htmlFor="removeMedia"
                className="text-sm cursor-pointer"
                style={{ color: theme.textColor + 'cc' }}
              >
                Remove media
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2 rounded-lg transition-all"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: theme.textColor,
                border: `1px solid ${theme.accentColor}40`,
                opacity: isSaving ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !content.trim()}
              className="flex-1 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.accentColor,
                color: theme.textColor
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
